import { createReducer, on } from '@ngrx/store';

import { RealtimeConnectionState } from '@app/core/realtime/realtime.service';
import {
  ConnectedDevice,
  DisconnectReason,
  IdentifiedPayload,
  IdentifyFailureReason,
} from '@app/core/realtime/realtime.types';
import * as RealtimeActions from './realtime.actions';

export const REALTIME_FEATURE_KEY = 'realtime';

export type RealtimeLastError =
  | { kind: 'identify'; reason: IdentifyFailureReason }
  | { kind: 'force-disconnect'; reason: DisconnectReason }
  | null;

export interface RealtimeState {
  connectionState: RealtimeConnectionState;
  socketInfo: IdentifiedPayload | null;
  devices: ConnectedDevice[];
  lastError: RealtimeLastError;
}

export const initialRealtimeState: RealtimeState = {
  connectionState: 'IDLE',
  socketInfo: null,
  devices: [],
  lastError: null,
};

export const realtimeReducer = createReducer(
  initialRealtimeState,

  on(RealtimeActions.realtimeConnect, state => ({
    ...state,
    connectionState: 'CONNECTING' as RealtimeConnectionState,
    lastError: null,
  })),

  on(RealtimeActions.realtimeDisconnect, state => ({
    ...state,
    connectionState: 'IDLE' as RealtimeConnectionState,
    socketInfo: null,
    devices: [],
  })),

  on(
    RealtimeActions.realtimeConnectionStateChanged,
    (state, { state: connectionState }) => ({
      ...state,
      connectionState,
    })
  ),

  on(RealtimeActions.realtimeConnected, (state, { socketInfo }) => ({
    ...state,
    connectionState: 'CONNECTED' as RealtimeConnectionState,
    socketInfo,
    lastError: null,
  })),

  on(RealtimeActions.realtimeIdentifyFailed, (state, { reason }) => ({
    ...state,
    connectionState: 'FAILED' as RealtimeConnectionState,
    socketInfo: null,
    devices: [],
    lastError: { kind: 'identify', reason } as RealtimeLastError,
  })),

  on(RealtimeActions.realtimeDeviceListReceived, (state, { devices }) => ({
    ...state,
    devices,
  })),

  on(RealtimeActions.realtimeDeviceConnected, (state, { device }) => {
    if (state.devices.some(d => d.socketId === device.socketId)) {
      return state;
    }
    return { ...state, devices: [...state.devices, device] };
  }),

  on(RealtimeActions.realtimeDeviceDisconnected, (state, { event }) => ({
    ...state,
    devices: state.devices.filter(d => d.socketId !== event.socketId),
  })),

  on(RealtimeActions.realtimeForceDisconnected, (state, { reason }) => ({
    ...state,
    connectionState: 'DISCONNECTED' as RealtimeConnectionState,
    socketInfo: null,
    devices: [],
    lastError: { kind: 'force-disconnect', reason } as RealtimeLastError,
  }))
);
